// O que cada bloqueio diz, e o que ele oferece em troca.
//
// Subiu de `src/components/LockedFeature/config.ts` quando o app nativo ganhou a mesma tela: o
// texto de um bloqueio é uma PROMESSA COMERCIAL, e duas listas de benefícios divergindo dariam
// ao mesmo plano descrições diferentes em cada superfície.
//
// O ícone ficou para trás de propósito: na web ele é um componente do `react-icons`, que não
// existe no app. Cada superfície escolhe o seu — e a tela da Nyta usa o emblema dela nas duas.

// ─── Types ────────────────────────────────────────────────────────────────────

// Variantes de bloqueio. A origem é uma só desde a unificação da cobrança: falta assinatura.
// (Antes eram duas — perfil pendente por cobrança única e falta de PRO —, e os nomes dos CTAs
// abaixo ainda carregam essa divisão porque a PROMESSA é diferente: um fala do perfil, o outro
// fala da conta.)
//
// Estas telas são o que o usuário grátis encontra andando pelo app. Ele ENTRA nos módulos e vê o
// que existe ali dentro, em vez de ser expulso para a tela de planos: a `/planos` promete que o
// grátis "visualiza músicas, agenda e equipe", e um redirecionamento fazia o app desmentir a
// própria página de preços.
export type LockedFeatureKey =
  | 'planning' | 'plano' | 'team' | 'tasks' | 'nyta' | 'refazer'
  | 'catalog' | 'agenda' | 'arquivos' | 'negociacoes' | 'shows' | 'cronograma' | 'perfil' | 'marketing';

export type LockedCtaKind = 'unlock-profile' | 'subscribe-pro';

/**
 * O QUE O CONVIDADO LÊ QUANDO O TITULAR NÃO RENOVOU.
 *
 * ⚠️ AQUI NÃO SE VENDE NADA, e é a diferença inteira para o `LOCKED_FEATURE_CONFIG` acima.
 *
 * Quem lê isto não é o pagador. O gate de um perfil compartilhado é o `owner_is_pro`, então se
 * esta pessoa assinar o PRO ela CONTINUA bloqueada — teria pago para não destravar nada. Um
 * botão para /planos aqui seria cobrança indevida, e das piores: a que não entrega.
 *
 * O texto não nomeia o titular nem diz "vencida"/"cancelada": o convidado não tem direito ao
 * estado da assinatura de outra pessoa, e os dois motivos possíveis (conta sem PRO, ou perfil
 * acima da cota contratada) chegam nele como a mesma consequência.
 */
export const AVISO_DO_TITULAR = {
  titulo: 'Este perfil está em somente leitura',
  corpo: 'O titular deste perfil está sem assinatura ativa. Você continua vendo tudo, mas as '
       + 'alterações não podem ser salvas até que a assinatura seja renovada.',
  apoio: 'Fale com quem te convidou: assim que a assinatura voltar, a edição libera na hora.',
  fechar: 'Entendi',
} as const;

/**
 * O aviso do convidado num módulo que o titular não liberou para ele (acesso por módulo, 03/10/2026).
 * O menu não esconde o módulo: a página diz que ele existe e quem pode liberar.
 */
export const AVISO_SEM_ACESSO = {
  titulo: 'Este módulo não foi liberado para você',
  corpo: 'Quem decide o que cada pessoa da equipe vê e edita é o titular do perfil.',
  apoio: 'Peça a quem te convidou para liberar este módulo na Equipe do perfil.',
  fechar: 'Entendi',
} as const;

/** O aviso do convidado que tenta salvar num módulo liberado só para Ver. */
export const AVISO_SO_VER = {
  titulo: 'Você pode ver este módulo, mas não editar',
  corpo: 'O titular do perfil liberou este módulo para você só em modo de leitura.',
  apoio: 'Para editar, peça a quem te convidou para mudar o seu acesso na Equipe do perfil.',
  fechar: 'Entendi',
} as const;

export interface LockedFeatureConfig {
  title: string;
  benefits: [string, string, string]; // exatamente 3
  cta: { label: string; kind: LockedCtaKind };
}

// ─── Configuration ────────────────────────────────────────────────────────────

// ⚠️ O RÓTULO É UM SÓ, e sem preço.
//
// Antes havia dois ("Desbloquear este perfil" e "Assine o PRO") e o componente ainda anexava
// "— R$ X/mês". Três telas do mesmo app pediam a mesma coisa com palavras e números diferentes,
// e o valor repetido em cada bloqueio fazia o app soar como cobrança em vez de convite. O preço
// tem uma casa, que é a /planos.
//
// O `kind` FICA, e não é redundante: ele é o DESTINO, não a promessa. O app nativo ainda vende o
// desbloqueio por perfil e roteia por ele (`AvisoDoPro`); na web os dois terminam em /planos.
// "Seja PRO" era curto demais para o momento: a pessoa acabou de ler o que o módulo faz, e o
// botão precisa fechar essa frase — dizer o que acontece ao clicar, e não só nomear o plano.
const ROTULO = 'Desbloqueie com o Maestra PRO';

const UNLOCK_PROFILE: LockedFeatureConfig['cta'] = { label: ROTULO, kind: 'unlock-profile' };
const SUBSCRIBE_PRO: LockedFeatureConfig['cta'] = { label: ROTULO, kind: 'subscribe-pro' };

export const LOCKED_FEATURE_CONFIG: Record<LockedFeatureKey, LockedFeatureConfig> = {
  planning: {
    title: 'Planejamento Estratégico',
    benefits: [
      'Diagnóstico completo da carreira com a Nyta',
      // "Salvo para sempre" era vocabulário do pagamento único, que não existe mais: numa
      // assinatura, prometer "para sempre" é prometer o que o cancelamento desmente.
      'Plano de ação personalizado, no ritmo da sua carreira',
      'Compartilhe o perfil com colaboradores',
    ],
    cta: UNLOCK_PROFILE,
  },
  // `planning` é o WIZARD (onde o plano nasce); `plano` é o Plano de Ação (o que se executa).
  // Eram a mesma chave, e a tela do plano de ação abria com o título do planejamento.
  plano: {
    title: 'Plano de Ação',
    benefits: [
      'As ações do seu plano, com prazo e responsável',
      'Progresso por fase, do que está em dia ao que atrasou',
      'O cronograma se distribui sozinho no seu calendário',
    ],
    cta: UNLOCK_PROFILE,
  },
  team: {
    title: 'Gestão de Equipe',
    benefits: [
      'Convide colaboradores para o perfil',
      'Eles acompanham planejamento, músicas e agenda',
      'Compartilhamento incluso no perfil completo',
    ],
    cta: UNLOCK_PROFILE,
  },
  tasks: {
    title: 'Gestão de Ações',
    benefits: [
      'Gerencie as ações do seu plano de ação',
      'Acompanhe o progresso fase a fase',
      'Recurso do plano PRO',
    ],
    cta: SUBSCRIBE_PRO,
  },
  // Refazer o diagnóstico é o loop de crescimento: executou o plano, cresceu, refaz o REAL e a
  // fase sobe. É PRO porque recalcular é o que dá sentido a acompanhar a evolução — quem faz uma
  // vez tem o retrato; quem refaz tem a trajetória.
  refazer: {
    title: 'Refazer o diagnóstico',
    benefits: [
      'Recalcule o Índice REAL quando a carreira mudar',
      'Acompanhe a evolução das quatro frentes ao longo do tempo',
      'O diagnóstico anterior fica salvo para comparação',
    ],
    cta: SUBSCRIBE_PRO,
  },
  nyta: {
    title: 'Nyta Assistente',
    benefits: [
      'Chat inteligente que executa ações no seu perfil',
      'Crie ações, eventos e gerencie músicas por conversa',
      'Análises e recomendações personalizadas com IA',
    ],
    cta: SUBSCRIBE_PRO,
  },
  catalog: {
    title: 'Músicas',
    benefits: [
      'Catálogo sem limite de faixas, com versões e pistas',
      'Ficha de cada gravação: BPM, tom, ISRC e colaboradores',
      'Espaço JAM para produzir junto com o seu time',
    ],
    cta: SUBSCRIBE_PRO,
  },
  agenda: {
    title: 'Agenda',
    benefits: [
      'O cronograma da carreira num calendário só',
      'Shows, gravações e lançamentos com responsáveis',
      'As ações do seu plano entram aqui automaticamente',
    ],
    cta: SUBSCRIBE_PRO,
  },
  // A promessa aqui é o ESPAÇO, não a tela: quem não assina tem cota zero e não sobe arquivo
  // nenhum, então prometer "veja seus arquivos" seria prometer uma lista vazia.
  arquivos: {
    title: 'Arquivos',
    benefits: [
      '30 GB para os áudios, capas e material dos seus lançamentos',
      'Veja o que cada perfil ocupa, do maior arquivo ao menor',
      'Libere espaço apagando o que nenhuma música usa',
    ],
    cta: SUBSCRIBE_PRO,
  },
  // ⚠️ ESTES TRÊS PROMETEM O QUE A PRIMEIRA FATIA JÁ ENTREGA, e não o módulo inteiro. A
  // precificação e a proposta pronta vêm nas fatias seguintes, e prometê-las agora seria vender
  // uma tela que o novo assinante não encontraria. Quando elas chegarem, estes textos mudam com
  // elas: uma vitrine que promete o que não existe é cobrança que não entrega.
  negociacoes: {
    title: 'CRM',
    benefits: [
      'Cada show que te chamam num quadro só, do primeiro contato ao sim',
      'A sua base de contratantes, com o histórico de cada um',
      'As tarefas da negociação já prontas, para nenhum contato esfriar',
    ],
    cta: SUBSCRIBE_PRO,
  },
  // ⚠️ SÓ O QUE A PRIMEIRA FATIA DO SHOWS JÁ ENTREGA: cadastro, tarefas na agenda e o que o show
  // deixa. Contrato, ficha do contratante e Cronograma do Show vêm depois, e prometê-los agora seria
  // vender uma tela que o novo assinante não encontraria.
  shows: {
    title: 'Shows',
    benefits: [
      'Cada show fechado num lugar só, com cachê, parcelas e quem contrata',
      'As tarefas de produção de cada show, com data, direto na sua agenda',
      'Quanto cada show deixa no seu bolso, mês a mês',
    ],
    cta: SUBSCRIBE_PRO,
  },
  // O CRONOGRAMA (03/11/2026): o papel do show para a equipe, só para ler. Não tem módulo no
  // convite: cada membro vê os shows em que está escalado (`cronogramas_da_equipe`).
  cronograma: {
    title: 'Cronograma',
    benefits: [
      'O cronograma de cada show para a banda, sem dar acesso à Produção',
      'Horários, endereço, quem vai, transporte e o que levar, num lugar só',
      'Cada pessoa da equipe vê só os shows em que está escalada',
    ],
    cta: SUBSCRIBE_PRO,
  },
  // O PERFIL (a página pública do artista) é do plano grátis (decisão do Lucas, 04/10/2026): o dono
  // entra sempre, e o que é do PRO nele é só o "Contrate meu show", que a própria tela trava. Esta
  // chave existe para o aviso do convidado sem acesso a Vendas sair com o ícone e o nome do Perfil,
  // e não com os de Vendas (o Perfil morava dentro do bloqueio de Vendas e herdava o desenho dele).
  perfil: {
    title: 'Perfil',
    benefits: [
      'A sua página pública, com a bio, os links e o seu som',
      'Contrate meu show: o pedido cai direto no seu funil de Vendas',
      'Um endereço só, para pôr no Instagram e no site',
    ],
    cta: SUBSCRIBE_PRO,
  },
  // ⚠️ SÓ O QUE A PARTE 1 DO MARKETING ENTREGA: o branding. Material de apresentação, divulgação
  // de shows, lançamentos e redes vêm nas partes seguintes, e prometê-los agora seria vender uma
  // aba que diz "em breve".
  marketing: {
    title: 'Marketing',
    benefits: [
      'O seu arquétipo, a palavra-chave e o tom de voz, num quiz guiado pela Nyta',
      'O documento de branding pronto para mandar ao fotógrafo, ao designer e à equipe',
      'As referências visuais, a persona e os pilares de conteúdo num lugar só',
    ],
    cta: SUBSCRIBE_PRO,
  },
};
